/**
 * client/src/App.tsx — the shell, and the front door.
 *
 * `<AppFrame>` is the chrome every screen except the reader sits inside. One header, one place: the
 * shelf drew its own for about an hour and that is exactly how a product ends up with three slightly
 * different headers. The reader is deliberately outside it — §5.1's frame is a fixed-size,
 * non-scrolling box and a sticky header above it would eat the measured `--frame-h`.
 *
 * ⚠️ **Mobile-first at 390 px.** books.karti.ai shipped a header that wanted 484 px of a 375 px
 * viewport for months: nothing errors, the page just drifts sideways under your thumb. Below `sm`
 * the nav is icons, the mark steps down a size, and "Upload" is a `+`.
 */

import { useState } from "react";
import { BrowserRouter, Link, NavLink, useLocation } from "react-router-dom";
import type { ReactElement, ReactNode } from "react";
import { isGod } from "../../shared/access.ts";
import { ShareNotice } from "./components/VisibilityMenu.tsx";
import { coverUrl } from "./lib/api.ts";
import { useAuth } from "./lib/auth.tsx";
import { hashId } from "./lib/id.ts";
import { ReadingTogetherRow, useMyRooms } from "./pages/Rooms.tsx";
import { AppRoutes } from "./routes.tsx";

/**
 * The router lives here and the route *table* lives in `routes.tsx`, because the table is the thing
 * that grows and the shell is the thing that must not.
 */
export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

// ─── the mark ────────────────────────────────────────────────────────────────

/**
 * The site mark. The dot is the one accent in the header and it means "this is us" — the same
 * reserved-colour discipline as the sibling repos, where a second use of the accent in one header is
 * a second meaning nobody agreed on.
 */
export function SiteMark({ small }: { small?: boolean }) {
  return (
    <Link to="/" className={small ? "ab-mark ab-mark-sm" : "ab-mark"} aria-label="audiobook.karti.ai — home">
      audiobook<span className="ab-mark-dot">.</span>karti.ai
    </Link>
  );
}

// ─── icons ───────────────────────────────────────────────────────────────────
// Hand-written, 16px, `currentColor`, `stroke-width: 1.6`. There is no icon package in this repo's
// dependency graph and adding one to draw six glyphs is 40 kB for six glyphs.

const svg = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", "aria-hidden": true } as const;
const stroke = { stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;

export const IconShelf = () => (
  <svg {...svg}>
    <path {...stroke} d="M4 5v14M4 19h16M8 15V7m4 8V5m4 10V9" />
  </svg>
);
export const IconPlus = () => (
  <svg {...svg}>
    <path {...stroke} d="M12 5v14M5 12h14" />
  </svg>
);
/** A slider bank — the admin screen is knobs and counters, not a book. */
export const IconAdmin = () => (
  <svg {...svg}>
    <path {...stroke} d="M4 6h16M4 12h16M4 18h16M9 4v4m6 2v4M7 16v4" />
  </svg>
);
export const IconBack = () => (
  <svg {...svg}>
    <path {...stroke} d="M15 5l-7 7 7 7" />
  </svg>
);
export const IconBook = () => (
  <svg {...svg}>
    <path {...stroke} d="M5 4h9a3 3 0 013 3v13H8a3 3 0 01-3-3V4zM8 20a3 3 0 01-3-3" />
  </svg>
);
export const IconCheck = () => (
  <svg {...svg}>
    <path {...stroke} d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);
export const IconAlert = () => (
  <svg {...svg}>
    <path {...stroke} d="M12 8v5m0 3.5v.01M10.3 4.3L2.9 17.2A2 2 0 004.6 20h14.8a2 2 0 001.7-2.8L13.7 4.3a2 2 0 00-3.4 0z" />
  </svg>
);

// ─── the frame ───────────────────────────────────────────────────────────────

export function AppFrame({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const { actor, signedIn, user, signOut } = useAuth();
  const { pathname } = useLocation();

  return (
    <div className="ab-app">
      <header className="ab-header">
        <div className="ab-header-inner">
          <div className="ab-header-left">
            <SiteMark small />
            {signedIn && (
              <nav className="ab-nav" aria-label="Main">
                <NavLink to="/" end className="ab-navlink" title="Shelf">
                  <span className="ab-navlink-icon">
                    <IconShelf />
                  </span>
                  <span className="ab-navlink-label">Shelf</span>
                </NavLink>
                {/* ⚠️ **LAW 13.** `isGod` from `shared/access.ts`, the same predicate every admin
                    route enforces with — never a build flag and never an email string compared here.
                    Absent rather than disabled: a link that exists and refuses tells a stranger the
                    surface is real, which is the whole reason those routes answer 404. */}
                {isGod(actor) && (
                  <NavLink to="/admin" className="ab-navlink ab-navlink-admin" title="Admin">
                    <span className="ab-navlink-icon">
                      <IconAdmin />
                    </span>
                    <span className="ab-navlink-label">Admin</span>
                  </NavLink>
                )}
              </nav>
            )}
          </div>

          <div className="ab-header-right">
            {signedIn ? (
              <>
                <Link
                  to="/upload"
                  className="ab-btn ab-btn-primary ab-btn-sm"
                  title="Upload a book"
                  aria-label="Upload a book"
                >
                  <IconPlus />
                  <span className="ab-btn-label">Upload</span>
                </Link>
                <button
                  type="button"
                  className="ab-btn ab-btn-ghost ab-btn-sm"
                  onClick={() => void signOut()}
                  title={user ? `Signed in as ${user.displayName || user.handle}` : undefined}
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to="/signin"
                state={{ from: pathname }}
                className="ab-btn ab-btn-primary ab-btn-sm"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className={wide ? "ab-main ab-main-wide" : "ab-main"}>
        {/* `GET /s/:token` redirects a dead link to `/?share=invalid`, and it does not know where
            that person will land — the shelf if they are signed in, the front door if they are not.
            Putting the notice inside the frame covers every signed-in screen at once; `<Landing>`
            renders its own copy for the other case. It draws nothing when the parameter is absent. */}
        <ShareNotice />
        {children}
      </main>
    </div>
  );
}

// ─── a book's face ───────────────────────────────────────────────────────────

/**
 * The cover, in one component, used by the shelf and by the book screen.
 *
 * ⚠️ One component and not two. books.karti.ai keeps a monument to the alternative: three
 * independent copies of one page grid, and when two were deleted the third kept its *names* and lost
 * its *geometry* — four of six layouts silently collapsed into one. Sharing the names is not sharing
 * the layout.
 *
 * §3.1 guarantees a cover always exists (the EPUB's own, else the first page rastered, else the
 * first image, else a generated typographic card), so the fallback below is for the window *before*
 * ingest finishes and for the request that fails — not for "books without covers", which do not
 * exist. The cloth colour is derived from the id, never random, so a book is the same object every
 * time you open the shelf.
 */
export function BookCover({
  bookId,
  title,
  author,
  coverSha,
  eager,
}: {
  bookId: string;
  title: string;
  author?: string | null;
  coverSha: string | null;
  eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const board = (hashId(bookId) % 8) + 1;

  if (!coverSha || failed) {
    return (
      <div className={`ab-cover ab-cover-typo ab-board-${board}`} role="img" aria-label={title}>
        <span className="ab-cover-title">{title}</span>
        {author ? <span className="ab-cover-author">{author}</span> : null}
      </div>
    );
  }
  return (
    <img
      className="ab-cover"
      // ⚠️ 512, not 400. `GET /api/v1/covers/:id.webp?w=` takes an ALLOW-LIST — 64/128/256/512/1024
      // — because an unbounded `?w=` on a resize endpoint is a CPU amplifier pointed at one box
      // (`server/src/routes/covers.ts`). A width off the list is a 400, and a 400 on an `<img>` is
      // indistinguishable from "not yours": `onError` below silently swapped in the typographic
      // fallback, so EVERY shelf card rendered as a coloured board with the real cover sitting one
      // HTTP status away and nothing in any log. Found in a browser, not in a test.
      //
      // 512 is the smallest listed width that still covers a 2× phone card (≈170 CSS px) and a 1×
      // desktop one, so one request serves both.
      src={coverUrl(bookId, 512)}
      // The cover route answers `readAccess` and refuses **404** rather than 403 (LAW 17), so a
      // failure here is indistinguishable from "not yours" by design. Falling back to the
      // typographic card is the only correct response: never render a broken-image glyph, and never
      // report "you can't see this" from an <img> that was told nothing.
      onError={() => setFailed(true)}
      alt={title}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      width={400}
      height={600}
    />
  );
}

/**
 * What `loading` renders. Not a spinner: the session answers in a few milliseconds on a warm
 * connection and a spinner that flashes for 40 ms is worse than a page that is simply the right
 * colour for 40 ms. It holds the frame height so nothing jumps when the real screen lands.
 */
export function Blank() {
  return <div className="ab-blank" aria-hidden />;
}

// ─── formatting, once ────────────────────────────────────────────────────────

/**
 * Bytes, in the units a person uses. **Decimal MB, not MiB**, because the number next to it in this
 * product is always something a human said out loud ("512 MB per file", "a 12-hour book is ~280 MB",
 * §5.8) and a 1.048576× discrepancy against the stated limit reads as a bug in the limit.
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1000) return `${n} B`;
  if (n < 1000 * 1000) return `${(n / 1000).toFixed(0)} kB`;
  if (n < 1000 * 1000 * 1000) return `${(n / (1000 * 1000)).toFixed(n < 10 * 1000 * 1000 ? 1 : 0)} MB`;
  return `${(n / (1000 * 1000 * 1000)).toFixed(1)} GB`;
}

/** "3 minutes ago". Falls back to a date past a week, where "9 days ago" stops being useful. */
export function timeAgo(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 45) return "just now";
  if (s < 90) return "a minute ago";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ${h === 1 ? "hour" : "hours"} ago`;
  const d = Math.round(h / 24);
  if (d <= 7) return `${d} ${d === 1 ? "day" : "days"} ago`;
  return new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** A small honest note about an unbuilt phase. The phase number is in the text so it greps. */
export function PhaseNote({ phase, children }: { phase: string; children: ReactNode }) {
  return (
    <p className="ab-phase-note">
      <span className="ab-chip ab-chip-soon">{phase}</span>
      <span>{children}</span>
    </p>
  );
}

// ─── the front door ──────────────────────────────────────────────────────────

/**
 * `/` for anyone not signed in.
 *
 * The copy claims only what is built. P1 reads; it does not yet speak, share or sync — and the
 * house rule from books.karti.ai is *only claim what is built*, with its expensive second half:
 * a "not built yet" list goes stale in the direction nobody checks, because no screen looks broken
 * while it under-promises. The three rows below carry their phase numbers so that when a phase
 * lands, a grep for `P2` across the tree finds this file.
 */
/**
 * The one book an anonymous visitor may open — *The Fourth Turning*, `visibility: "public"`.
 *
 * A constant rather than a fetch: `<Landing>` renders before any network call and must keep doing
 * so. If this book is ever taken private the link degrades to the book screen's own honest refusal,
 * which is a far better failure than a front door that waits on a request to decide what to show.
 */
const SAMPLES: readonly { id: string; title: string; author: string; note: string }[] = [
  {
    id: "bk_32qzhr66hrppk303",
    title: "The Fourth Turning",
    author: "William Strauss & Neil Howe",
    note: "History as four turnings, and where this one ends.",
  },
  {
    id: "bk_2wt997agoryoo3ly",
    title: "Mind",
    author: "Daniel J. Siegel",
    note: "16 hours, narrated end to end. A journey to the heart of being human.",
  },
];

function IconPlay() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
      <path d="M4.5 2.6v10.8a.6.6 0 0 0 .92.51l8.4-5.4a.6.6 0 0 0 0-1.02l-8.4-5.4a.6.6 0 0 0-.92.5Z" />
    </svg>
  );
}

export function Landing() {
  return (
    <div className="ab-landing">
      <header className="ab-landing-head">
        <SiteMark />
        <Link to="/signin" className="ab-btn ab-btn-primary ab-btn-sm">
          Sign in
        </Link>
      </header>

      {/* A dead share link lands here for anyone not signed in — which is most of the people who get
          sent one. Without this the redirect is a front door with no explanation for why they are
          looking at it instead of a book. */}
      <ShareNotice />

      {/* ⚠️ **§7.6's third way back, for the person most likely to need it.**

          A guest's room seat is durable — a disconnect never removes a member row — and
          `GET /api/v1/rooms/mine` answers correctly for a `gst_` key. But a signed-out actor gets
          this screen rather than the shelf, so the seat the server knows about had nowhere to be
          rendered, and `/r/CODE` in a text message was the only route back. The row renders nothing
          at all when the list is empty, which is every stranger who ever lands here. */}
      <GuestRooms />

      <section className="ab-hero">
        <div className="ab-hero-copy">
        <h1 className="ab-hero-title">
          A book you can read,
          <br />
          and a voice that reads it to you.
        </h1>
        {/* ⚠️ This used to open "Upload an EPUB", which is the one thing the reader of this page
            cannot do: the site is invite-only, so a stranger is being sold a door they have no key
            to before they have been shown the room. It now leads with what they can do here, in this
            tab, with no account — and mentions uploading last, as what an account is FOR. */}
        <p className="ab-hero-sub">
          The sentence being spoken lights up. The page turns itself, one column at a time, and your
          place is a sentence rather than a page number — so it survives every change of font size,
          every device, every night you fall asleep mid-chapter. Bring your own EPUBs once
          you&rsquo;re in.
        </p>
        {/* ⚠️ **The samples are the primary action, and sign-in is secondary — deliberately.**

            Everything above this line is a claim. A stranger who lands here cannot check any of it
            without an invite, so the page's whole job is to hand them the books they ARE allowed to
            open. `canSee`/`canList` already admit an anonymous actor to a `public` book, and the anon
            path was verified end to end on 2026-08-14: the shelf lists it logged out, the planner
            answers `ready`, and `/api/v1/audio/page/…` returns real AAC.

            Hard-coded rather than fetched, because `<Landing>` must stay renderable without a fetch
            — the same reason `<GuestRooms>` is a separate component. A dead id here degrades to a
            book screen that says so, not to a blank front door.

            ⚠️ Two is a choice; a list is a shelf. If this ever grows past three or four it should
            become a real fetch of `scope=public` sorted by coverage, because at that point a
            hand-maintained array is a second copy of the shelf that goes stale silently. */}
        <ul className="ab-samples">
          {SAMPLES.map((s) => (
            <li key={s.id}>
              <Link to={`/b/${s.id}/read`} className="ab-sample">
                <span className="ab-sample-play" aria-hidden="true">
                  <IconPlay />
                </span>
                <span className="ab-sample-text">
                  <span className="ab-sample-title">{s.title}</span>
                  <span className="ab-sample-author">{s.author}</span>
                  <span className="ab-sample-note">{s.note}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="ab-hero-note">
          Narrated in Karti&rsquo;s voice. No account, nothing to install —{" "}
          <Link to="/signin">sign in</Link> to bring your own books.
        </p>
        </div>

        {/* The product in one picture, and the same drawing as the link-preview card so the page a
            visitor lands on matches the unfurl that brought them. Decorative: `aria-hidden`, and it
            is the first thing dropped below the two-column breakpoint — a phone should spend its
            height on the sentence and the button, not on an illustration of a page. */}
        <div className="ab-hero-art" aria-hidden="true">
          <div className="ab-page-card">
            <div className="ab-page-ch">Chapter One</div>
            {[100, 94, 99, 72, 97, 88, 96, 61, 93, 99, 78].map((w, i) => (
              <div key={i} className={`ab-page-ln${i === 2 || i === 3 ? " ab-page-lit" : ""}`} style={{ width: `${w}%` }} />
            ))}
            <div className="ab-page-bar">
              <span />
            </div>
          </div>
        </div>
      </section>

      <section className="ab-landing-grid" aria-label="What this is">
        <article className="ab-landing-card">
          <h2>It reads</h2>
          <p>
            One column per screen, turned rather than scrolled. Change the font size mid-page and you
            land on the same sentence — because your place is a sentence, never a page number.
          </p>
        </article>

        <article className="ab-landing-card">
          <h2>It speaks</h2>
          <p>
            Every sentence narrated and cached by what it says, so fixing one word costs one sentence
            of GPU and never moves your place.
          </p>
        </article>

        <article className="ab-landing-card">
          <h2>It&rsquo;s shareable</h2>
          <p>
            Send someone a link and they read and listen with no account at all — the voice, the
            highlight, the page turning itself. Take the book back and they lose it on the next page.
          </p>
        </article>

        <article className="ab-landing-card">
          <h2>Together</h2>
          <p>
            The same chapter on two devices on a Tuesday night. One of you pauses, both pause, and
            tomorrow it offers to pick up where you stopped.
          </p>
        </article>
      </section>

      <footer className="ab-landing-foot">
        <span>audiobook.karti.ai</span>
        <span className="ab-dot-sep">·</span>
        <span>a private library</span>
      </footer>
    </div>
  );
}

/**
 * The rooms a signed-out reader is already sitting in, on the front door.
 *
 * ⚠️ **A separate component, because `<Landing>` must stay renderable without a fetch.** Putting
 * `useMyRooms()` inline would make the front door — the first paint a stranger ever sees — wait on a
 * network call that answers `{rooms: []}` for almost everybody. Here the hook lives inside a
 * component that renders `null` the moment the answer is empty, which it is until a guest has
 * actually been in a room.
 *
 * `unbuilt` and `error` are not surfaced: this is a courtesy strip on a marketing page, and an error
 * box about a rooms API is noise on a screen whose job is to explain what the product is.
 */
function GuestRooms(): ReactElement | null {
  const mine = useMyRooms();
  if (mine.loading || mine.unbuilt || mine.rooms.length === 0) return null;
  return (
    <section className="ab-landing-rooms">
      <ReadingTogetherRow rooms={mine.rooms} />
    </section>
  );
}
